import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import './ZoomModal.css'; // Make sure you have styles for .modal-loading-spinner
import useGlobalStore from '../../store/globalStore';

// --- Import Close Button Image ---
import closeButtonImage from '../../assets/UI/button_close.svg'; // Import close image
// --- Import Zoom Button Images ---
import zoomInImage from '../../assets/UI/button_zoom_in.svg'; // Adjust path if needed
import zoomOutImage from '../../assets/UI/button_zoom_out.svg'; // Adjust path if needed
import qrCodeImage from '../../assets/UI/button_QR_scan.svg'; // Adjust path if needed

import testVTT from '../../assets/test.vtt';

import { fixURL } from '../../utils/urlUtils';

import parse from 'html-react-parser';


// Accept 'type' prop, default to 'image'
const ZoomModal = forwardRef(({ modalContent, isOpen, onClose, onQR }, ref) => {
  // Destructure modalContent to get assetSrc, type, caption, and id.
  // Provide default values for type, caption, and id if they are not present in modalContent,
  // or if modalContent itself is null/undefined.
  const { src: assetSrc, type = 'image', caption = '', id = '', srt=null, qr='' } = modalContent || {};
  const transformWrapperRef = useRef(null);
  // Add state to track current scale
  const [currentScale, setCurrentScale] = useState(0.75);
  // assetRef will now be managed by a callback ref pattern
  const [assetNode, setAssetNode] = useState(null);
  const assetCallbackRef = useCallback(node => {
    if (node !== null) {
      console.log("[ZoomModal assetCallbackRef] DOM node set:", node);
      setAssetNode(node);
    } else {
      console.log("[ZoomModal assetCallbackRef] DOM node cleared (unmounted).");
      // When the element unmounts (e.g. due to key change or conditional rendering),
      // clear the assetNode state. This helps the loading effect re-evaluate correctly.
      setAssetNode(null); // Let's test without this first to see if key prop is enough.
                           // If issues persist with stale assetNode, we can re-enable this.
    }
  }, []); // Empty dependency array means this callback itself is stable

  const isElectron = useGlobalStore((state) => state.isElectron);
  const isSmallMobile = useGlobalStore((state) => state.isSmallMobile);
  const isDesktop = useGlobalStore((state) => state.isDesktop);
  //const audioQrCode = useGlobalStore((state) => state.audioQrCode);
  const setAudioQrCode = useGlobalStore((state) => state.setAudioQrCode);

  const [isLoading, setIsLoading] = useState(true);
  const modalOverlayRef = useRef(null); // Ref for the overlay div

  // State to control rendering and animation classes
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [animationClass, setAnimationClass] = useState('');

  // State to hold the asset details for display, persists during fade-out
  const [displayAsset, setDisplayAsset] = useState({ src: null, type: 'image', caption: '', id: '', srt:null, qr:'' });

  //console.log("assetSrc", assetSrc);
  //console.log("displayAsset", displayAsset, id);


  // --- Reset Zoom Effect ---
  // Only relevant for image type
  useEffect(() => {
    // Check if shouldRender is false, indicating modal is fully closed or closing
    if (type === 'image' && !shouldRender && transformWrapperRef.current) {
      // Reset zoom/pan when modal closes
      transformWrapperRef.current.resetTransform(0);
    }
  }, [shouldRender, type]);

  // Effect to update displayAsset when props change
  useEffect(() => {
    if (isOpen && assetSrc) {
      // Only update displayAsset if a new, valid asset is coming in
      // and it's different from what's currently set for display (or if displayAsset.src is null or id is different)
      if (assetSrc !== displayAsset.src || type !== displayAsset.type || caption !== displayAsset.caption || id !== displayAsset.id || srt !== displayAsset.srt || qr !== displayAsset.qr) {
        console.log("[ZoomModal DisplayAssetEffect] Updating displayAsset from modalContent:", { assetSrc, type, caption, id, srt, qr });
        setDisplayAsset({ src: assetSrc, type, caption, id, srt, qr });
        setIsLoading(true); // Assume new asset needs loading
      }
    } else if (!isOpen) {
      // When modal is closing, don't clear displayAsset here.
      // It will be naturally reset if a new assetSrc comes when it reopens.
      // We could reset isLoading here if needed, but the loading effect handles it.
      // If you want displayAsset to clear when modal is not open (even during fade-out start), you could add:
      // else if (!isOpen && displayAsset.src) { setDisplayAsset({ src: null, type: 'image', caption: '', id: '' }); }
    }
  }, [isOpen, assetSrc, type, caption, id, srt, qr, displayAsset.src, displayAsset.type, displayAsset.caption, displayAsset.id, displayAsset.qr]);


  // --- Loading State and Event Listener Effect ---
  useEffect(() => {
    // This effect now depends on displayAsset.src
    console.log(`[ZoomModal LoadingEffect] Triggered. isOpen: ${isOpen}, displayAsset.src: ${displayAsset.src}, assetNode: ${assetNode ? 'present' : 'null'}`);

    if (!isOpen || !displayAsset.src) {
      console.log("[ZoomModal LoadingEffect] Condition: Not open or no displayAsset.src. Resetting isLoading if not open.");
      if (!isOpen) setIsLoading(true); // Reset isLoading for next open, only if modal is actually closed
      return; // No need to add listeners if closed or no asset to display
    }

    // If assetNode (the actual DOM element) is not yet available, wait.
    if (!assetNode) {
      console.log("[ZoomModal LoadingEffect] Condition: assetNode is null. Waiting for ref to be set. Spinner remains.");
      // Ensure isLoading is true if we expect an assetNode but don't have it yet
      if (!isLoading) setIsLoading(true);
      return;
    }

    console.log("[ZoomModal LoadingEffect] Condition: assetNode IS available. Proceeding with listeners/check for:", assetNode, "Current displayAsset.type:", displayAsset.type);
    let isMounted = true; // Flag to prevent state updates on unmounted component

    // Define load/error handlers
    const handleLoad = () => {
      if (isMounted) {
        console.log(`[ZoomModal LoadingEffect - Event] Asset loaded via '${loadEvent}' event for ${displayAsset.src}. Setting isLoading=false.`);
        setIsLoading(false);
      }
    };
    const handleError = (e) => {
      if (isMounted) {
        console.error("[ZoomModal LoadingEffect - Event] Error loading asset:", displayAsset.src, e);
        setIsLoading(false); // Hide spinner even on error
      }
    };

    const loadEvent = displayAsset.type === 'image' ? 'load' : 'canplay';

    // Check if already complete (cached)
    let isInitiallyComplete = false;
    if (displayAsset.type === 'image') {
      isInitiallyComplete = assetNode.complete && assetNode.naturalHeight !== 0;
      console.log(`[ZoomModal LoadingEffect - Check] Image check for ${displayAsset.src}: complete=${assetNode.complete}, naturalHeight=${assetNode.naturalHeight}`);
    } else if (displayAsset.type === 'video') {
      isInitiallyComplete = assetNode.readyState >= 2; // HAVE_METADATA or more
      console.log(`[ZoomModal LoadingEffect - Check] Video check for ${displayAsset.src}: readyState=${assetNode.readyState}`);
    }

    if (isInitiallyComplete) {
      console.log("[ZoomModal LoadingEffect - Check] Asset already complete (cached). Setting isLoading=false.");
      setIsLoading(false);
    } else {
      // If not initially complete, add listeners
      console.log(`[ZoomModal LoadingEffect - Listeners] Asset ${displayAsset.src} not initially complete. Adding listeners for ${loadEvent} to:`, assetNode);
      assetNode.addEventListener(loadEvent, handleLoad);
      assetNode.addEventListener('error', handleError);
    }

    // Cleanup function: runs when component unmounts or dependencies change
    return () => {
      isMounted = false;
      if (assetNode) {
        console.log(`[ZoomModal LoadingEffect Cleanup] Removing listeners for ${loadEvent} from element for ${displayAsset.src}:`, assetNode);
        assetNode.removeEventListener(loadEvent, handleLoad);
        assetNode.removeEventListener('error', handleError);
      }
    };
  }, [isOpen, displayAsset.src, displayAsset.type, assetNode, isLoading]); // Added isLoading to re-evaluate if it was true and assetNode became available

  // --- Effect to handle modal open/close animations ---
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true); // Ensure modal is in DOM
      // Short delay to allow DOM update before adding class for transition
      const fadeInTimer = setTimeout(() => {
        setAnimationClass('open');
      }, 10); // Small delay for CSS transition to catch up
      return () => clearTimeout(fadeInTimer);
    } else {
      setAnimationClass(''); // Remove 'open' class to trigger fade-out
    }
  }, [isOpen]);

  // --- Effect to handle actual unmounting after fade-out ---
  useEffect(() => {
    const overlayNode = modalOverlayRef.current;
    if (!overlayNode) return;

    const handleTransitionEnd = (event) => {
      // Make sure we're listening to the opacity transition on the overlay
      if (event.target === overlayNode && event.propertyName === 'opacity') {
        if (!isOpen && animationClass === '') { // If it was fading out
          console.log("ZoomModal: Fade-out transition ended. Setting shouldRender to false.");
          setShouldRender(false); // Now actually remove from DOM
        }
      }
    };

    overlayNode.addEventListener('transitionend', handleTransitionEnd);
    return () => {
      overlayNode.removeEventListener('transitionend', handleTransitionEnd);
    };
  }, [isOpen, animationClass]); // Dependencies

  // --- Zoom Handlers (Only needed for image type) ---
  const handleZoomIn = (event) => {
    event.stopPropagation(); // Prevent overlay click
    if (transformWrapperRef.current) {
      transformWrapperRef.current.zoomIn(0.5);
    }
  };

  const handleZoomOut = (event) => {
    event.stopPropagation(); // Prevent overlay click
    if (transformWrapperRef.current) {
      transformWrapperRef.current.zoomOut(0.5);
    }
  };

  const handleTransformChange = useCallback((ref) => {
    setCurrentScale(ref.state.scale);
  }, []);
  // --- End Zoom Handlers ---

  // Handle closing when clicking the overlay
  const handleOverlayClick = (event) => {
    // Ensure the click is directly on the overlay, not on its children (like the content)
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleQRClick = useCallback(() => {
  //const handleQRClick = (event) => {
    setAudioQrCode(modalContent.qr);

    console.log("handleQRClick", modalContent.qr, assetNode);
    if(assetNode) assetNode.pause();
    onQR(modalContent.qr);
    //if() setAudioQrCode
    // setTimeout(() => {
    //     onQR(modalContent.qr);
    //   }, 1000);
    
  }, [ modalContent]);

  // Add this method to be called from parent
  const onQRClose = () => {
    console.log("QR video closed - executing ZoomModal function");
    // Example: Refresh content or trigger other logic
    // Add your custom logic here
    if(assetNode) assetNode.play();
  };
  // Expose method via ref
  useImperativeHandle(ref, () => ({
    onQRClose,
    isVideoPlaying: () => displayAsset.type === 'video' && assetNode && !assetNode.paused
  }));

  // FIXME: this is not firing
  const onVideoComplete = (e) => {
    console.log("onVideoComplete",e);
    onClose();
  };

  // Helper to safely render HTML for caption
  const createMarkup = (htmlString) => {
    return { __html: htmlString || "" };
  };



  // Don't render anything if the modal is not open
  if (!shouldRender) { // Use shouldRender to control DOM presence
    return null;
  }

  // Combine class names cleanly for the content container
  const containerClass = displayAsset.type === 'video' ? 'modal-video-container' : 'modal-image-container';
  const loadedClass = (isOpen && !isLoading) ? 'loaded' : ''; // Content is loaded if modal is open and not loading
  const videoControlsList = (isElectron) ? 'nofullscreen nodownload noplaybackrate' : 'nodownload noplaybackrate'; // dont show fullscreen button in electron app- breaks the custom zoom
  // Update button disabled states
  const isMinScale = currentScale <= 0.5;
  const isMaxScale = currentScale >= 3;
  return (
    <div ref={modalOverlayRef} className={`modal-overlay ${animationClass}`} onClick={handleOverlayClick}>
      {/* Use stopPropagation on the content wrapper click to prevent closing when clicking inside */}
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>

        <div className={`bottom-grad ${loadedClass}`}></div>

        {/* --- Loading Indicator --- */}
        {/* Show spinner if modal is intended to be open AND isLoading is true */}
        {(isOpen && isLoading) && (
          <div className="modal-loading-spinner">
            {/* Replace with a more sophisticated spinner component if desired */}
            <div className="spinner"></div> {/* Example basic spinner */}
            {/* <span>LOADING...</span> */}
          </div>
        )}

        {/* --- Conditional Rendering based on type --- */}
        {/* Render content container only if displayAsset.src is available */}
        {displayAsset.src && (
          <>
            {displayAsset.type === 'video' ? (
              // --- Video Container ---
              <>
              <div className={`${containerClass} ${loadedClass}`}>
                {/* New wrapper for the video element and QR code */}
                <div className="video-element-wrapper">
                  <video
                    ref={assetCallbackRef} // Use callback ref
                    key={displayAsset.src} // Force re-render on src change
                    src={ fixURL(displayAsset.src) }
                    controls
                    controlsList={videoControlsList}
                    disablePictureInPicture
                    autoPlay // Consider making this optional via prop
                    crossOrigin="anonymous"
                    className="popup-video"
                    preload="metadata" // Good default: loads dimensions, duration etc.
                    // Event listeners are now handled programmatically in useEffect
                    onEnded={onVideoComplete}
                  >
                    {/* <track
                    label="English"
                    kind="subtitles"
                    srclang="en"
                    src={testVTT}
                    default /> */}
                    
                    { displayAsset.srt &&
                      <track
                    label="English"
                    kind="subtitles"
                    srcLang="en"
                    src={displayAsset.srt}
                    crossOrigin="anonymous"
                    default /> }
                  </video>
                  {/* {displayAsset.srt && <span>{displayAsset.srt}</span>}  */}
                </div>
                 
              </div>

              {/* QR code is now OUTSIDE the video-element-wrapper */}
              {(isElectron && displayAsset.qr !== '') && (
                <div className="qr-code-container" onClick={handleQRClick}>
                <div >
                  <img src={qrCodeImage} alt="QR Code" /> Access online audio
                </div>
              </div>
              )}
              </>
            ) : (
              // --- Image Container ---
              <>
              <div className={`${containerClass} ${loadedClass}`}>
                <TransformWrapper
                  key={`${displayAsset.src}-${isDesktop}`} // Force re-render on src change OR isSmallMobile change
                  ref={transformWrapperRef}
                  initialScale={!isDesktop ? 1 : 0.75}
                  limitToBounds={true}
                  minScale={0.5}
                  maxScale={3}
                  wheel={{ step: 0.25 }}
                  centerOnInit={true}
                  centerZoomedOut={true} // Center the image if 'contain' makes it smaller
                  doubleClick={{ mode: "zoomIn" }}
                  disabled={isLoading && isOpen} // Disable zoom/pan if modal is open and loading
                  onTransformed={handleTransformChange}
                >
                  <TransformComponent
                    wrapperStyle={{ width: "100vw", height: "100vh" }}
                    contentStyle={{ width: "100vw", height: "100vh" }}
                  >
                    <img
                      ref={assetCallbackRef} // Use callback ref
                      src={ fixURL(displayAsset.src)}
                      alt={displayAsset.caption || "Popup view"}
                      className="popup-image"
                      // loading={isElectron ? undefined : "lazy"} // Conditionally apply lazy loading
                    />
                  </TransformComponent>
                </TransformWrapper>
              </div>

              {/* FIXED: take zoom buttons OUT of the image container otherwise we get a weird zindex glitch during fade in */}
              {/* --- Zoom Buttons (Only for image type) --- */}
              {(isOpen && !isLoading) && ( // Show zoom buttons if modal is open and not loading
                <div className="modal-zoom-controls">
                  <button
                    onClick={handleZoomIn}
                    disabled={isMaxScale}
                    // className="zoom-button zoom-in"
                    className={`zoom-button zoom-in ${isMaxScale ? 'disabled' : ''}`}
                    aria-label="Zoom In"
                    tabIndex='503'
                  >
                    <img src={zoomInImage} alt="Zoom In" />
                  </button>
                  <button
                    onClick={handleZoomOut}
                    disabled={isMinScale}
                    className={`zoom-button zoom-out ${isMinScale ? 'disabled' : ''}`}
                    // className="zoom-button zoom-out"
                    aria-label="Zoom Out"
                    tabIndex='504'
                  >
                    <img src={zoomOutImage} alt="Zoom Out" />
                  </button>                    
                </div>
              )}
              {/* --- End Zoom Buttons --- */}
              </>
            )}
          </>
        )}
        {/* --- End Conditional Rendering --- */}

        {/* --- Caption Display (Render if caption exists and not loading) --- */}
        {false && displayAsset.caption && (isOpen && !isLoading) && ( // Show caption if available, modal open, and not loading
          <div className="modal-caption" >
            {parse(displayAsset.caption)}
          </div>
        )}
        {/* <div className="modal-caption">ID: {displayAsset.id}</div> */}


        <button className={`close-button close-button-anim ${animationClass} ${isElectron ? 'is-electron' : ''}`} onClick={onClose} aria-label="Close popup" tabIndex='505'>
          Close <img src={closeButtonImage} alt="Close" />
        </button>
      </div>
    </div>
  );
});

export default ZoomModal;
